import { trackStoreeelEvent } from '@/helpers/trackStoreeelEvent';
import { buildMediaAssetIdParam } from '@/helpers/buildMediaAssetIdParam';
import { getSwiperInstance } from '@/helpers/swiperInstance'; // ← Swiperインスタンス取得関数

export const trackMediaProcess = () => {
  const swiper = getSwiperInstance(); // Swiperインスタンスを取得
  if (!swiper) return;

  let video = null;
  let observer = null;
  let timeupdateHandler = null;
  let endedHandler = null;

  const clearActiveVideoTracking = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (video && timeupdateHandler) {
      video.removeEventListener('timeupdate', timeupdateHandler);
    }
    if (video && endedHandler) {
      video.removeEventListener('ended', endedHandler);
    }
    video = null;
    timeupdateHandler = null;
    endedHandler = null;
  };

  const setActiveVideo = (targetVideo) => {
    // 画像スライドへ遷移した場合も追跡対象を解除し、同じ動画へ戻った時に再初期化できるようにする
    if (!targetVideo) {
      clearActiveVideoTracking();
      return;
    }
    if (targetVideo === video) return;

    clearActiveVideoTracking();
    video = targetVideo;
    initVideoTracking(video);
  };

  const initVideoTracking = (targetVideo) => {
    if (!targetVideo) return;

    targetVideo.setAttribute('data-reached-checkpoints', '[]');

    const checkpoints = [20, 40, 60, 80, 100];

    // 到達した進捗チェックポイントを（重複なく）送信する共通処理。
    // progress 以下の未送信チェックポイントをまとめて送るため、timeupdate と ended の双方から再利用できる。
    const sendReachedCheckpoints = (progress) => {
      const alreadyReached = JSON.parse(targetVideo.getAttribute('data-reached-checkpoints') || '[]');
      const mediaName = targetVideo.getAttribute('media-name') || '';
      // 計測は「実際に表示中のスライド」基準で判定する。
      // window.moviePosition ベースのインデックス計算は頻度挿入スライドの差し込みで
      // 位置がズレ、プロモを通常コンテンツと誤判定する（通常 storeeel_movie_progress に
      // MediaAsset の hashid が混入する）ため、DOM の wrapper dataset を正とする。
      const wrapper = targetVideo.closest('.slide-media-wrapper');
      const mediaAssetType = wrapper?.dataset?.assetType;
      const isFrequencyInsertContent = wrapper?.dataset?.isFrequencyInsertContent === 'true';
      const frequencyInsertContentId = wrapper?.dataset?.frequencyInsertContentId || '';

      checkpoints.forEach((point) => {
        if (progress >= point && !alreadyReached.includes(point)) {
          alreadyReached.push(point);
          trackStoreeelEvent(isFrequencyInsertContent ? 'storeeel_frequency_insert_movie_progress' : 'storeeel_movie_progress', {
            'storeeel_name': mediaName,
            'media_progress': point,
            ...buildMediaAssetIdParam(
              isFrequencyInsertContent
                ? (frequencyInsertContentId || targetVideo.getAttribute('media-id'))
                : targetVideo.getAttribute('media-id'),
              mediaAssetType
            ),
            ...(isFrequencyInsertContent ? {} : { 'campaign_id': window.storeeelItems.id }),
          });
        }
      });

      targetVideo.setAttribute('data-reached-checkpoints', JSON.stringify(alreadyReached));
    };

    timeupdateHandler = () => {
      if (!targetVideo.duration) return;
      const currentProgress = (targetVideo.currentTime / targetVideo.duration) * 100;
      sendReachedCheckpoints(currentProgress);
    };

    // 動画終了時は currentTime が duration に届かず timeupdate で 100% を踏めないことが多いため、
    // ended で 100%（と未送信の下位チェックポイント）を確実に送る。重複は data-reached-checkpoints で防止。
    endedHandler = () => {
      sendReachedCheckpoints(100);
    };

    targetVideo.addEventListener('timeupdate', timeupdateHandler);
    targetVideo.addEventListener('ended', endedHandler);

    observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
          targetVideo.setAttribute('data-reached-checkpoints', '[]');
        }
      }
    });

    observer.observe(targetVideo, { attributes: true, attributeFilter: ['src'] });
  };

  const handleSlideChange = () => {
    const newVideo = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .storeeel-movie');
    setActiveVideo(newVideo);
  };

  const handleActiveVideoChanged = (event) => {
    const candidate = event?.detail?.video;
    if (candidate && candidate.classList?.contains('storeeel-movie')) {
      setActiveVideo(candidate);
      return;
    }
    handleSlideChange();
  };

  swiper.on('slideChangeTransitionEnd', handleSlideChange);
  window.addEventListener('storeeel-active-video-changed', handleActiveVideoChanged);

  // 初回実行
  handleSlideChange();
};
